/**
 * Alles, was auch ohne Scrollytelling gebraucht wird:
 * Dialoge und Formulare, Galerie, Seitenübergang, nächstes Event nach Datum, Farbton der Kopfzeile, Magnet-Buttons.
 */

const html = document.documentElement;
const reduced = matchMedia("(prefers-reduced-motion: reduce)").matches;
const emit = (open: boolean) => document.dispatchEvent(new CustomEvent("nccc:modal", { detail: open }));

/* ───────────── Dialoge ───────────── */

function openDialog(d: HTMLDialogElement) {
  if (d.open) return;
  d.showModal();
  emit(true);
  const first = d.querySelector<HTMLElement>(".sheet-foot .btn, [data-close]");
  first?.focus({ preventScroll: true });
}

function closeDialog(d: HTMLDialogElement) {
  if (!d.open || d.classList.contains("is-closing")) return;
  if (reduced) { d.close(); return; }
  d.classList.add("is-closing");
  setTimeout(() => { d.classList.remove("is-closing"); d.close(); }, 330);
}

export function initDialogs() {
  document.addEventListener("click", (e) => {
    const t = e.target as HTMLElement;
    const opener = t.closest<HTMLElement>("[data-open]");
    if (opener) {
      const d = document.getElementById(opener.dataset.open!) as HTMLDialogElement | null;
      if (d) { e.preventDefault(); openDialog(d); }
      return;
    }
    const closer = t.closest<HTMLElement>("[data-close]");
    if (closer) { const d = closer.closest("dialog"); if (d) closeDialog(d); return; }
    if (t instanceof HTMLDialogElement && t.open) {
      // Klick auf die Fläche außerhalb schließt
      const r = t.getBoundingClientRect();
      const ev = e as MouseEvent;
      if (ev.clientX < r.left || ev.clientX > r.right || ev.clientY < r.top || ev.clientY > r.bottom) closeDialog(t);
    }
    const gal = t.closest<HTMLElement>("[data-gallery]");
    if (gal) openGallery(gal.dataset.gallery!);
    const disabled = t.closest<HTMLElement>("[aria-disabled='true']");
    if (disabled) e.preventDefault();
  });
  document.querySelectorAll("dialog").forEach((d) => {
    d.addEventListener("close", () => emit(false));
    d.addEventListener("cancel", (e) => { e.preventDefault(); closeDialog(d as HTMLDialogElement); });
  });
}

function openGallery(no: string) {
  const tpl = document.querySelector<HTMLTemplateElement>(`template[data-gallery-for="${CSS.escape(no)}"]`);
  const d = document.getElementById("dlg-gallery") as HTMLDialogElement | null;
  if (!tpl || !d) return;
  d.querySelector(".sheet-h")!.textContent = tpl.dataset.title ?? no;
  const gal = d.querySelector(".gal")!;
  gal.replaceChildren(tpl.content.cloneNode(true));
  openDialog(d);
  d.scrollTop = 0;
}

/* ───────────── E-Mail-Vorlagen kopieren ───────────── */

export function initMailTemplates() {
  document.addEventListener("click", async (e) => {
    const b = (e.target as HTMLElement).closest<HTMLButtonElement>("[data-copy-mail]");
    if (!b) return;
    const d = b.closest("dialog");
    if (!d) return;
    const to = d.querySelector("[data-mail-to]")?.textContent?.trim() ?? "";
    const subject = d.querySelector("[data-mail-subject]")?.textContent?.trim() ?? "";
    const body = d.querySelector("[data-mail-body]")?.textContent ?? "";
    const text = `${to}\n${subject}\n\n${body}`;
    const msg = d.querySelector<HTMLElement>(".form-msg");
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Fallback ohne Clipboard-API: Text markieren, damit man ihn selbst kopieren kann
      const r = document.createRange();
      r.selectNodeContents(d.querySelector("[data-mail-body]")!);
      const sel = getSelection();
      sel?.removeAllRanges();
      sel?.addRange(r);
      document.execCommand?.("copy");
    }
    if (msg) { msg.textContent = b.dataset.done ?? ""; msg.classList.add("is-ok"); }
  });
}

/* ───────────── Kapitelliste am Fingerabdruck ───────────── */

export function initChapters() {
  const box = document.querySelector<HTMLElement>("[data-chapters]");
  if (!box) return;
  const btn = box.querySelector<HTMLButtonElement>("button")!;
  const list = box.querySelector<HTMLElement>(".chapter-list")!;
  const links = [...list.querySelectorAll<HTMLAnchorElement>("a[data-ch]")];
  const isOpen = () => btn.getAttribute("aria-expanded") === "true";
  const mark = () => {
    const y = innerHeight / 2;
    let cur = "";
    for (const a of links) {
      const r = document.getElementById(a.dataset.ch!)?.getBoundingClientRect();
      if (r && r.top <= y && r.bottom > y) cur = a.dataset.ch!;
    }
    links.forEach((a) => (a.dataset.ch === cur ? a.setAttribute("aria-current", "true") : a.removeAttribute("aria-current")));
  };
  const open = () => {
    mark();
    list.hidden = false;
    btn.setAttribute("aria-expanded", "true");
    requestAnimationFrame(() => list.classList.add("is-open"));
  };
  const close = (focusBtn = false) => {
    list.classList.remove("is-open");
    btn.setAttribute("aria-expanded", "false");
    setTimeout(() => { if (!isOpen()) list.hidden = true; }, 380);
    if (focusBtn) btn.focus();
  };
  btn.addEventListener("click", () => (isOpen() ? close() : open()));
  list.addEventListener("click", (e) => { if ((e.target as HTMLElement).closest("a")) close(); });
  document.addEventListener("click", (e) => { if (isOpen() && !box.contains(e.target as Node)) close(); });
  document.addEventListener("keydown", (e) => { if (e.key === "Escape" && isOpen()) close(true); });
}

/* ───────────── Plakate: Kippeffekt und Klick ───────────── */

export function initPosters() {
  // Plakat im Archiv (auch im 3D-Tunnel) führt zur Event-Seite
  document.addEventListener("click", (e) => {
    const card = (e.target as HTMLElement).closest<HTMLElement>(".t-card[data-href]");
    if (!card) return;
    const link = document.querySelector<HTMLAnchorElement>(`.ev-page[href="${card.dataset.href}"]`);
    if (link) link.click(); else location.href = card.dataset.href!;
  });
  if (html.classList.contains("motion") || reduced || !matchMedia("(hover: hover)").matches) return;
  document.querySelectorAll<HTMLElement>("[data-tilt]").forEach((el) => {
    const glare = el.querySelector<HTMLElement>(".glare");
    el.addEventListener("pointermove", (ev) => {
      const r = el.getBoundingClientRect();
      const x = (ev.clientX - r.left) / r.width - 0.5, y = (ev.clientY - r.top) / r.height - 0.5;
      el.style.transform = `perspective(1400px) rotateX(${(-y * 7).toFixed(2)}deg) rotateY(${(x * 10).toFixed(2)}deg)`;
      glare?.style.setProperty("--gx", `${(x + 0.5) * 100}%`);
      glare?.style.setProperty("--gy", `${(y + 0.5) * 100}%`);
    });
    el.addEventListener("pointerleave", () => { el.style.transform = ""; });
  });
}

/* ───────────── Zurück zur Startseite an die alte Stelle ───────────── */

export function initBackLinks() {
  document.querySelectorAll<HTMLAnchorElement>("a[data-back]").forEach((a) => {
    a.addEventListener("click", (e) => {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      let ref: URL | null = null;
      try { ref = document.referrer ? new URL(document.referrer) : null; } catch { ref = null; }
      const home = new URL(a.href).pathname;
      // Kam man von der Startseite, führt „Zurück“ genau an die Stelle, von der man kam
      if (ref && ref.origin === location.origin && ref.pathname === home && history.length > 1) {
        e.preventDefault();
        history.back();
      }
    });
  });
}

/* ───────────── Nächstes Event nach Datum ───────────── */

export function initNextEvent() {
  const over = (el: HTMLElement | null) => !!el?.dataset.end && Date.now() >= new Date(el.dataset.end).getTime();
  // Leiste unter dem Hero verschwindet nach dem Event
  document.querySelectorAll<HTMLElement>(".nextbar").forEach((b) => { if (over(b)) b.remove(); });
  // Event-Seite des kommenden Termins: Anmeldung weg, Hinweis und Wege ins Archiv
  const up = document.querySelector<HTMLElement>(".evp--upcoming .evp-in");
  if (over(up)) {
    up!.querySelector(".evp-register")?.remove();
    up!.querySelector<HTMLElement>(".evp-over")?.removeAttribute("hidden");
  }
  const sec = document.getElementById("next");
  if (!sec || !over(sec)) return;
  sec.querySelector(".next-inner")?.remove();
  sec.querySelector<HTMLElement>(".next-none")?.removeAttribute("hidden");
  sec.dataset.theme = "dark";
  sec.classList.remove("next--token-break", "next--meetup");
  document.querySelectorAll(".hn-title, .hn-date").forEach((n) => n.remove());
}

/* ───────────── Farbton der Kopfzeile ───────────── */

export function initTone() {
  const els = [...document.querySelectorAll<HTMLElement>("[data-theme]")];
  const foot = document.querySelector<HTMLElement>(".foot-word");
  let last = "";
  const probe = () => {
    const y = 44;
    let tone = "dark";
    for (const el of els) {
      const r = el.getBoundingClientRect();
      if (r.top <= y && r.bottom > y) {
        const th = el.dataset.themeLive ?? el.dataset.theme;
        tone = th === "pink" ? "pink" : "dark";
      }
    }
    if (foot) {
      const r = foot.getBoundingClientRect();
      if (r.top < innerHeight * 0.92) tone = tone === "pink" ? "pink" : "hide";
    }
    if (tone !== last) { html.dataset.tone = tone; last = tone; }
  };
  let queued = false;
  const onScroll = () => { if (!queued) { queued = true; requestAnimationFrame(() => { queued = false; probe(); }); } };
  addEventListener("scroll", onScroll, { passive: true });
  addEventListener("resize", onScroll);
  document.addEventListener("nccc:tone", onScroll);
  probe();
}

/* ───────────── Buttons: Füllung ab Zeigerposition, Magnet ───────────── */

export function initButtons() {
  document.addEventListener("pointerover", (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>(".btn");
    if (!b) return;
    const r = b.getBoundingClientRect();
    b.style.setProperty("--mx", `${e.clientX - r.left}px`);
    b.style.setProperty("--my", `${e.clientY - r.top}px`);
  });
  if (reduced || !matchMedia("(hover: hover)").matches) return;
  document.querySelectorAll<HTMLElement>("[data-magnetic]").forEach((el) => {
    let x = 0, y = 0, tx = 0, ty = 0, raf = 0;
    const tick = () => {
      x += (tx - x) * 0.14; y += (ty - y) * 0.14;
      el.style.translate = `${x.toFixed(2)}px ${y.toFixed(2)}px`;
      if (Math.abs(tx - x) + Math.abs(ty - y) > 0.05) raf = requestAnimationFrame(tick);
      else raf = 0;
    };
    const kick = () => { if (!raf) raf = requestAnimationFrame(tick); };
    el.addEventListener("pointermove", (e) => {
      const r = el.getBoundingClientRect();
      tx = (e.clientX - r.left - r.width / 2) * 0.22;
      ty = (e.clientY - r.top - r.height / 2) * 0.32;
      kick();
    });
    el.addEventListener("pointerleave", () => { tx = 0; ty = 0; kick(); });
  });
}
